import { WorkspaceNavigationService } from '../../../services/workspace-navigation.service';
import { DataTableCellDirective } from '../../../shared/ui/data-table/data-table-cell.directive';
import { DataSelectionBinding, DataViewSelection } from '../../../models/data-selection';
import { Optional } from '@angular/core';
import { viewContextKey, viewContextVisible } from '../../../shared/view-context';
import { formatDate } from '@angular/common';
import { DataListComponent, DataListSortOption } from '../../../shared/ui/data-list/data-list.component';
import { DataFetch, DataPage, DataQuery } from '../../../models/data-source';
import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, Input, OnChanges, OnDestroy, OnInit, QueryList, SimpleChanges, ContentChildren, TemplateRef, ViewChild, ViewChildren } from '@angular/core';
import { LeadPageComponent } from './lead-page.component';
import { HttpClient } from '@angular/common/http';
import { ActivatedRoute, Router } from '@angular/router';
import { CdkDragDrop } from '@angular/cdk/drag-drop';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { forkJoin, from, Observable, of, Subscription } from 'rxjs';
import {
  catchError, concatMap, finalize, map, tap, toArray,
} from 'rxjs/operators';
import { AuthService } from '../../../services/auth.service';
import { DataTableColumn, DataTableComponent, DataTableServerSort } from '../../../shared/ui/data-table/data-table.component';
import {
  EMPTY_LEAD_MAP_FILTER, LeadFeature, LeadMapFilter,
} from '../../../map/features/leads/lead-feature';
import {
  LEAD_LIST_COLUMNS, leadActionOverdue, leadSourceLabel,
} from './lead-list-table';
import { canonicalStage } from './legacy-stage-names';
import { leadRuleFilterFields } from './lead-filters';
import {
  ColumnSort, COLUMN_SORTS, LeadsBoardStateService, UndoableMove,
} from './leads-board-state.service';
import {
  MilestoneStop,
} from '../../../components/milestone-rail/milestone-rail.component';
import { stageColor } from './stage-colors';
import { businessTypeLabels } from './business-types';
import { LeadStageTransition, legalMoveCodes } from './stage-moves';
import { ApprovalRow } from '../approvals/approvals-panel.component';
import {
  ExportScope,
} from '../../../components/table-export-dialog/table-export-dialog.component';
import { localDateStringOffsetDays, todayLocalDateString } from '../../../shared/date.util';
import { CsvColumn } from '../../../shared/csv-export';
import { TableExport } from '../../../shared/table-export';
import { toRuleParams } from '../../../shared/ui/data-filter/data-filter.rules';
import {
  DATA_FILTER_OPS, isRule,
} from '../../../shared/ui/data-filter/data-filter.component';
import type {
  DataFilterField, DataFilterRule, DataFilterValue, DataFilterValues,
} from '../../../shared/ui/data-filter/data-filter.component';
import {
  CurrentSurfaceActionService,
  LeadTableViewAction,
  SurfaceActionApplyResult,
} from '../../../services/current-surface-action.service';
import { AppContextService } from '../../../services/app-context.service';
import { LeadApiService, LeadProfile } from '../../../services/lead-api.service';
import { deleteAllData } from '../../../shared/delete-all-data';

interface LeadTableViewSnapshot {
  ruleFilters: DataFilterValues;
  listSort?: DataTableServerSort;
}

export interface AssignableRep {
  userId: number;
  userName: string | null;
  displayName: string | null;
}

export interface WorkItem {
  leadId: number;
  itemType: string;
  /** What the column is called. Print this. */
  boardColumn: string;
  /** Which column this is. Compare and group on this. */
  boardColumnCode: string;
  /** `open` | `won` | `lost`. */
  boardColumnOutcome: string;
  title: string | null;
  salesRepUserId: number | null;
  stageEnteredAt: string | null;
  probability: number | null;
  qualificationOutcome: string | null;
  qualificationScore: number | null;
  routeFitStatus: string | null;
  contributionLow: number | null;
  contributionBase: number | null;
  contributionHigh: number | null;
  nextActionType: string | null;
  nextActionPlace: string | null;
  nextActionNote: string | null;
  nextActionDue: string | null;
  /** On the wire all along; declared so the export can write them. */
  lat: number | null;
  lng: number | null;
  expectedAnnualRevenue: number | null;
  updatedAt: string;
  // Lead fields folded into the board/list response.
  origin: string;
  leadStage: string;
  sourceCount: number;
  /** Distinct `place.source` values behind the accepted links: osm/google/user. */
  sourceKinds: string[];
  address: string | null;
  city: string | null;
  state: string | null;
  postalCode: string | null;
  /** The lead's business categories by store_type name, ordered. */
  businessTypes: string[];
  discoveredAt: string;
}

interface Facet {
  /** The column's identity, matching `WorkItem.boardColumnCode`. */
  column: string;
  label: string;
  count: number;
  contribution: number;
  share: number;
}
interface FacetsResponse { totalCount: number; columns: Facet[]; }
interface BoardColumnPage {
  page: number;
  total: number;
  loading: boolean;
  error: string;
  dirty: boolean;
  request?: Subscription;
}

interface ListResponse { total: number; items: WorkItem[]; }
export interface LeadStage {
  id: number;
  /** The tenant's wording for this stage. Display only. */
  name: string;
  /** The stage's identity. Every comparison on this page uses it. */
  code: string;
  /** `open` | `won` | `lost` - stated by the stage, not guessed from `name`. */
  outcome: string;
  vocabulary: string;
  description: string | null;
  isActive: boolean;
  isSystem: boolean;
  sortOrder: number;
}
/** One row of the board. `salesRepUserId` is set only when the rows are
 *  grouped by sales rep, and is what makes a row a legal reassignment target
 *  (design 3c). */
interface BoardRow { key: string; label: string; salesRepUserId?: number | null; }

/** A date as a number to sort on, or null when there is no date at all -
 *  including a string the browser cannot read, which must not become 0 and
 *  sort as the oldest thing on the board. */
function stamp(value: string | null): number | null {
  if (!value) return null;
  const at = Date.parse(value);
  return Number.isNaN(at) ? null : at;
}

/**
 * Leads Board / List (runway PR-14, deepened per 06 §5.3/5.5/5.6):
 * drag = transition PREVIEW (the card never moves until the command
 * succeeds), row grouping, WIP over-limit visuals from stage config,
 * compact/detailed density, and a List with row selection + scope-labeled
 * export.
 */
@Component({
  selector: 'app-leads-board',
  templateUrl: './leads-board.component.html',
  styleUrls: ['./leads-board.component.css'],
})
export class LeadsBoardComponent implements OnChanges, OnInit, AfterViewInit, OnDestroy {
  /** The same collection templates can be narrowed by map-owned Lead IDs. */
  @Input() selectedIds: readonly number[] | null = null;
  @Input() excludeSelectedIds = false;
  @Input() selectionBinding: DataSelectionBinding | null = null;
  /** Embedded picker: search and select Leads without collection management. */
  @Input() selectionOnly = false;
  /** The embedding picker owns contextual membership labels; Lead selection stays shared. */
  @Input() selectionStatus: TemplateRef<{ $implicit: WorkItem }> | null = null;

  /** Embedded, server-scoped collection; the parent owns its business scope and edits. */
  @Input() scopedFetch: DataFetch<WorkItem> | null = null;
  @Input() collectionKey = 'leads-table';
  @Input() pageSizeOptions = [25, 50, 100, 200];
  @Input() selectable = true;
  @Input() originLabel: string | null = null;
  /** Complete BR menu template for an embedded collection; the trigger supplies the row context. */
  @Input() rowActionMenu: TemplateRef<{ $implicit: WorkItem }> | null = null;
  @Input() rowExtras: TemplateRef<{ $implicit: WorkItem }> | null = null;
  @Input() bulkActions: TemplateRef<{ $implicit: DataViewSelection<WorkItem> }> | null = null;
  @ContentChildren(DataTableCellDirective) cells!: QueryList<DataTableCellDirective<WorkItem>>;
  get embedded(): boolean { return this.selectionOnly || !!this.scopedFetch; }
  cellTemplate(id: string): TemplateRef<{ $implicit: WorkItem }> | null {
    return this.cells?.find(cell => cell.columnId === id)?.template ?? null;
  }
  refresh(): void { this.listTable?.refresh(); this.compactList?.refresh(); }

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes['selectedIds'] && !changes['selectedIds'].firstChange)
      || (changes['excludeSelectedIds'] && !changes['excludeSelectedIds'].firstChange)) {
      this.load(true, this.state.view === 'board');
    }
  }
  @ViewChildren('boardViewport, boardStage', { read: ElementRef })
  private boardGeometry!: QueryList<ElementRef<HTMLElement>>;
  private boardResize?: ResizeObserver;
  private boardGeometryChanges?: Subscription;

  ngAfterViewInit(): void {
    this.boardResize = new ResizeObserver(() => this.refreshBoardOverflow());
    const observe = () => {
      this.boardResize!.disconnect();
      this.boardGeometry.forEach(ref => this.boardResize!.observe(ref.nativeElement));
      this.refreshBoardOverflow();
    };
    this.boardGeometryChanges = this.boardGeometry.changes.subscribe(observe);
    observe();
  }

  private refreshBoardOverflow(): void {
    this.boardGeometry.forEach(({ nativeElement }) => {
      if (nativeElement.classList.contains('rb-board')) this.updateBoardOverflow(nativeElement);
    });
  }

  updateBoardOverflow(viewport: HTMLElement): void {
    // Compare the visible column edge: stable scrollbar gutters can make
    // scrollWidth - clientWidth overstate the remaining distance at the end.
    const contentRight = viewport.lastElementChild?.getBoundingClientRect().right ?? 0;
    const hasMore = contentRight - viewport.getBoundingClientRect().right > 1;
    viewport.parentElement?.classList.toggle('rb-board-frame--more', hasMore);
  }

  @ViewChild('compactList') compactList?: DataListComponent<WorkItem>;
  @ViewChild('compactLeadPage') compactLeadPage?: LeadPageComponent;

  readonly compactSortOptions: readonly DataListSortOption[] = [
    { label: 'Name A–Z', field: 'title' },
    { label: 'Name Z–A', field: 'title', desc: true },
    { label: 'Stage', field: 'boardColumn' },
    { label: 'Next action due', field: 'nextActionDue' },
    { label: 'Recently discovered', field: 'discoveredAt', desc: true },
  ];

  editCompactLead(row: WorkItem): void {
    if (!this.canManageLeads || !this.compactList) return;
    this.compactList.expandedItem = row;
    // Mount the lazy detail before invoking its existing edit workflow.
    this.changeDetector?.detectChanges();
    this.compactLeadPage?.edit();
  }

  leadStages: LeadStage[] = [];
  /** The business categories a lead can be filtered to. */
  businessTypes: { id: number; name: string }[] = [];
  /** The declared moves between them. Empty means the vocabulary constrains
   *  nothing, which is where the lead vocabulary stands and why the board
   *  behaved correctly for leads while guessing wrong for roll-off. */
  stageTransitions: LeadStageTransition[] = [];

  readonly boardPageSize = 25;
  readonly boardPages: Record<string, BoardColumnPage> = {};
  boardMovesPending = 0;
  /** List columns: pipeline fields + everything the retired Prospects page
   *  showed (status / origin / sources / address / types / discovered). The
   *  selection checkbox is the table's own, not a column here.
   *
   *  Declared in `lead-list-columns.ts` because the Territories page shows the
   *  same list under a patch, and two copies drift. */
  @Input() listColumns: readonly DataTableColumn[] = LEAD_LIST_COLUMNS;
  items: WorkItem[] = [];
  facets: Facet[] = [];
  totalCount = 0;
  listTotal = 0;                    // server total for the current filter
  loadError: string | null = null;

  /** The server page size; the List pages through ALL data at this stride. */
  readonly pageSize = 200;
  /** Server page used by the Board's current lens. */
  page = 1;
  /** Monotonic guard so a slow response from an earlier filter never wins. */
  private loadSeq = 0;

  // The move panel: opened by Enter on a card or by a
  // drop that could not be carried out. There is no `moveTo` beside these -
  // a chosen target is acted on, not held pending a Confirm.
  moveTarget: WorkItem | null = null;
  moveError: string | null = null;
  moveNote = '';

  /**
   * Agent-proposed actions this board's leads are waiting on, handed up by the
   * approvals strip so one read serves the strip, the card markers and the
   * drawer. Empty for an account that may not approve anything - the strip's
   * own 403 lands here as "nothing waiting", which is true for that person.
   */
  pendingApprovals: ApprovalRow[] = [];

  onPendingApprovals(rows: ApprovalRow[]): void {
    this.pendingApprovals = rows;
  }

  /** How many decisions this lead is holding, or null so the card's marker
   *  disappears entirely rather than reading "0 awaiting approval". */
  approvalsFor(leadId: number): number | null {
    const count = this.pendingApprovals
      .filter(row => row.objectId === leadId).length;
    return count > 0 ? count : null;
  }

  /**
   * The case commands: owner, next action, and stage.
   *
   * The union the two endpoints behind them accept, not just the first half of
   * it. Mirroring only `LeadPipeline.Manage` made this client stricter than the
   * server it speaks to, which is the harmful direction: it hides a command the
   * API would have accepted, and nothing errors, so the rep concludes the
   * product cannot do it.
   */
  get canManagePipeline(): boolean {
    return this.auth.hasPermission('LeadPipeline.Manage')
      || this.auth.hasPermission('Lead.Manage');
  }

  /** The lead itself: contacts, activity, and deactivation. */
  get canManageLeads(): boolean {
    return this.auth.hasPermission('Lead.Manage');
  }

  /** Territories for the "my patch" filter. Empty until they exist. */
  territories: { id: number; name: string }[] = [];

  /** The reps the assignment view (3c) rows by. Empty until loaded, or when
   *  this account may not read the roster — the Board then falls back to the
   *  owner ids it can see on the cards themselves. */
  reps: AssignableRep[] = [];
  /**
   * Column rules offered under the filter bar's "+".
   *
   * Every id here is a query parameter /api/lead-work-items already honours,
   * so a rule narrows the whole result set rather than the loaded page. A
   * column the server cannot filter on is deliberately absent: an option that
   * quietly changes nothing is worse than one that is not offered.
   *
   * None is pinned. The filters people reach for daily already have their own
   * controls on this bar - search, Territory, Assigned to me - and a second
   * way to ask the same question is how two controls end up disagreeing.
   */
  filterFields: DataFilterField[] = [];

  // Assignment preview (design 3c): dragging a card into another rep's row is
  // a WRITE, so it gets the same treatment as a stage transition — the card
  // never moves, a confirmation panel opens on the card, and only a successful
  // command reloads the board.
  assignTarget: WorkItem | null = null;
  assignTo: BoardRow | null = null;
  assignError: string | null = null;
  assigning = false;


  private myUserId: number | null = null;

  /** What the map layer was last told, so an unchanged filter does not refetch it. */
  private mapFilterKey: string | null = null;
  private unregisterSurface?: () => void;
  private agentContextRegistration?: Subscription;

  constructor(
    private http: HttpClient,
    private auth: AuthService,
    public state: LeadsBoardStateService,
    private route: ActivatedRoute,
    private router?: Router,
    private snackBar?: MatSnackBar,
    private dialog?: MatDialog,
    private surfaceActions?: CurrentSurfaceActionService,
    private agentContext?: AppContextService,
    private leadApi?: LeadApiService,
    private changeDetector?: ChangeDetectorRef,
    private readonly leadLayer?: LeadFeature,
    @Optional() private readonly contextElement?: ElementRef<HTMLElement>,
    private readonly workspaceNavigation?: WorkspaceNavigationService,
  ) {}

  ngOnInit(): void {
    if (this.embedded) this.state.view = 'list';
    // A lead clicked inside a map popover comes back here. The layer
    // filters to its own objects, so this page hears only about leads.
    this.revealRequests = (this.scopedFetch ? undefined : this.leadLayer?.revealRequests$)
      ?.subscribe(objectId => this.revealFromMap(objectId));
    this.agentContextRegistration?.unsubscribe();
    this.agentContextRegistration = this.agentContext?.registerView({
      key: viewContextKey(this.route, this.scopedFetch ? `leads-collection:${this.collectionKey}` : 'leads-collection'), name: 'Leads collection', priority: -10,
      active: () => viewContextVisible(this.contextElement),
      read: () => ({ view: this.state.view, search: this.state.search,
        columns: this.state.columns, territoryId: this.state.territoryId,
        territoryMine: this.state.territoryMine, assignedToMeOnly: this.state.assignedToMeOnly,
        highContributionOnly: this.state.highContributionOnly, rowGroup: this.state.rowGroup,
        selectionOwner: this.selectionBinding?.key ?? null,
        selectedLeadIds: this.selectionBinding ? [] : this.listSelection.map(row => row.leadId) }),
      describe: state => {
        const ids = state.selectedLeadIds.slice(0, 200);
        const selected = state.selectionOwner
          ? `Selection follows ${state.selectionOwner}; see Map object selection.`
          : ids.length
          ? `Selected Lead IDs (${ids.length}${state.selectedLeadIds.length > ids.length ? ` of ${state.selectedLeadIds.length}` : ''}): ${JSON.stringify(ids)}.`
          : 'No Lead rows are selected.';
        return `Current Leads collection view: ${state.view === 'list' ? 'List' : 'Board'}. ${selected} `
          + JSON.stringify({ search: state.search, columns: state.columns, territoryId: state.territoryId,
            territoryMine: state.territoryMine, assignedToMeOnly: state.assignedToMeOnly,
            highContributionOnly: state.highContributionOnly, rowGroup: state.rowGroup, filters: this.state.ruleFilters });
      },
      restore: state => {
        this.state.search = state.search;
        this.state.columns = state.columns;
        this.state.territoryId = state.territoryId;
        this.state.territoryMine = state.territoryMine;
        this.state.assignedToMeOnly = state.assignedToMeOnly;
        this.state.highContributionOnly = state.highContributionOnly;
        this.state.rowGroup = state.rowGroup;
        this.load();
      },
    });
    if (this.scopedFetch) return;
    this.unregisterSurface?.();
    this.unregisterSurface = this.surfaceActions?.register({
      surface: { id: 'leads', view: this.state.view },
      apply: action => this.applyAgentTableView(action),
      restore: snapshot => this.restoreAgentTableView(snapshot),
    });
    // Column deep-link (?column=Pricing, e.g. from a Command Center funnel
    // row). Only a known column applies. No param leaves whatever filter
    // context the user already had (06 §4.5).
    //
    // Translated before the known-column guard below sees it. A bookmark made
    // before Proposal -> Presentation carries a name no active stage has, so
    // without this it fails that guard and is dropped in silence: the user
    // asked for one column and gets the whole board, with nothing saying why.
    // legacy-stage-names.ts exists for exactly this and was never wired in.
    const linked = this.route.snapshot.queryParamMap.getAll('column');
    if (linked.length) this.state.columns = [...new Set(linked.map(canonicalStage))];
    this.myUserId = this.auth.tenant.currentUserId;
    // The edges are loaded beside the catalog rather than gated on it: an
    // empty list is the honest reading of "this vocabulary constrains
    // nothing", which is also what a failed read would leave behind, and that
    // is the behaviour every lead has always had. A roll-off board whose edges
    // did not arrive offers too much and the server refuses the difference.
    this.http.get<LeadStageTransition[]>('/api/lead-stages/transitions').subscribe({
      next: edges => { this.stageTransitions = edges; },
      error: () => { this.stageTransitions = []; },
    });
    this.http.get<LeadStage[]>('/api/lead-stages').subscribe({
      next: rows => {
        this.leadStages = rows.filter(row => row.isActive)
          .sort((a, b) => a.sortOrder - b.sortOrder);
        this.buildFilterFields();   // the Stage rule lists the tenant's own words
        this.state.columns = this.state.columns.filter(code => this.leadStages.some(row => row.code === code));
        this.load();
      },
      error: (err: { status?: number }) => {
        // The status is the difference between "your role lacks a permission"
        // (403) and "the server broke" (500). Swallowing it cost a real hunt:
        // a 500 that hit every caller was chased through schema, data and
        // deploy health before anyone could see which kind of failure it was.
        this.loadError = `Failed to load lead stages (HTTP ${err.status ?? '?'}).`;
      },
    });
    this.http.get<{ id: number; name: string }[]>('/api/territories', { params: { status: 'Active' } })
      .subscribe({
        next: rows => { this.territories = rows; },
        error: () => { /* no territories yet, or no access: the filter stays hidden */ },
      });
    // Who a card may be reassigned to (3c). Admin + Sales only, and readable
    // with the Board's own LeadWorkItem.View — an account that gets an empty list
    // simply sees no extra rows and cannot reassign, which is the honest state.
    this.http.get<AssignableRep[]>('/api/sales-routes/reps').subscribe({
      next: rows => { this.reps = rows; this.buildFilterFields(); },
      error: () => { /* no roster: rows fall back to the owner ids on the cards */ },
    });
    // The Types rule lists the vocabulary itself; a failed read leaves the
    // dropdown empty, which offers nothing rather than offering nonsense.
    this.http.get<{ id: number; name: string }[]>('/api/lookup/business-types')
      .subscribe({
        next: rows => { this.businessTypes = rows; this.buildFilterFields(); },
        error: () => { /* the Types filter simply has nothing to offer */ },
      });
    this.buildFilterFields();
  }

  ngOnDestroy(): void {
    this.boardResize?.disconnect();
    this.boardGeometryChanges?.unsubscribe();
    ++this.loadSeq;
    for (const page of Object.values(this.boardPages)) page.request?.unsubscribe();
    // The layer outlives this page. Leaving it narrowed would hide leads from
    // every other screen that draws them.
    if (!this.scopedFetch) this.leadLayer?.filter(EMPTY_LEAD_MAP_FILTER);
    this.unregisterSurface?.();
    this.unregisterSurface = undefined;
    this.agentContextRegistration?.unsubscribe();
    this.agentContextRegistration = undefined;
    this.revealRequests?.unsubscribe();
    this.revealRequests = undefined;
  }

  /**
   * Rebuilt when the roster arrives rather than computed in a getter: the
   * template reads this on every change-detection pass, and a fresh array
   * each time would re-enter the filter bar's ngOnChanges for no reason.
   */
  /** A row's categories, written for a person and joined for one cell. */
  typeLabels(names: string[] | null | undefined): string {
    return businessTypeLabels(names ?? []).join(', ');
  }

  /** The Leads columns, from the list the Territories page's leads table
   *  shares (lead-filters.ts); rebuilt as the stages, types and roster land. */
  private buildFilterFields(): void {
    this.filterFields = leadRuleFilterFields(this.businessTypes, this.leadStages, this.reps);
  }

  /** Columns are keyed by code; this is where the tenant's wording comes
   *  back in. A code with no stage behind it prints as itself rather than
   *  blank - an old link is better answered visibly than silently. */
  columnLabel(column: string): string {
    return this.leadStages.find(stage => stage.code === column)?.name ?? column;
  }

  stageId(stageCode: string): number | null {
    return this.leadStages.find(stage => stage.code === stageCode)?.id ?? null;
  }

  /** One control carries both territory choices, because naming a patch and
   * asking for "all of mine" are mutually exclusive. */
  get territorySelection(): number | 'mine' | null {
    return this.state.territoryMine ? 'mine' : this.state.territoryId;
  }
  set territorySelection(value: number | 'mine' | null) {
    this.state.territoryMine = value === 'mine';
    this.state.territoryId = value === 'mine' ? null : value;
  }

  get compactTerritoryLabel(): string {
    if (this.state.territoryMine) return 'My territories';
    return this.territories.find(territory => territory.id === this.state.territoryId)?.name ?? 'All territories';
  }

  /** The territory-owner filter sent to the server. */
  get activeTerritoryOwner(): number | null {
    return this.state.territoryMine ? this.myUserId : null;
  }

  /** "My territories" is only offered when the token says who I am. */
  get canFilterMyTerritories(): boolean {
    return this.myUserId != null;
  }

  /**
   * The filter the whole page agrees on, as query parameters.
   *
   * One builder for three callers - the Board's load, the List table's fetch
   * and the export's page walk - because an export claiming a scope the table
   * on screen does not have is exactly the drift three copies invite.
   *
   * The contribution threshold is deliberately NOT here. The Board prunes its
   * loaded rows client-side while the List and export are database-paged and
   * must ask the server, so each states its own.
   */
  private filterParams(): Record<string, string | string[]> {
    const params: Record<string, string | string[]> = {};

    // One repeated `rule` parameter, each `field:op:value`. The server holds
    // the whitelist of columns and comparisons and answers 400 on anything
    // else, so a rule this page invents is a visible failure rather than a
    // filter that quietly does nothing.
    //
    // An Owner rule is dropped while "Assigned to me" is on: both narrow the
    // same column, and sending both would AND them into an empty result
    // rather than answering either question.
    const rules = toRuleParams(
      this.state.ruleFilters, undefined,
      this.state.assignedToMeOnly ? ['salesRep'] : []);
    if (rules.length) params['rule'] = rules;

    if (this.state.search.trim()) params['search'] = this.state.search.trim();
    if (this.state.assignedToMeOnly && this.myUserId != null)
      params['salesRep'] = String(this.myUserId);
    // The stage filter is server-side, so every view (and every
    // page of the List) sees the same filtered set - not just the loaded slice.
    if (this.state.columns.length) params['column'] = this.state.columns;
    if (this.state.territoryId != null) params['territoryId'] = String(this.state.territoryId);
    if (this.activeTerritoryOwner != null)
      params['territoryOwner'] = String(this.activeTerritoryOwner);
    return params;
  }

  /**
   * The Leads layer on the map beside this page is narrowed the way this
   * collection is.
   *
   * The two halves of the screen used to answer different questions: the list
   * showed the leads somebody asked for, and the map beside it showed all of
   * them.
   *
   * Stage, owner, search, territory and the contribution floor are not merely
   * similar on the two sides - the list and the map reach the same predicate,
   * LeadQueries.FilterActivePipeline, through their own endpoints. So what is
   * sent here narrows the map exactly as it narrows the rows.
   *
   * A filter-bar rule on any other column has no equivalent on the map
   * endpoint. Guessing at one would mean a map that quietly drops leads the
   * list is showing, so it is left out and the map stays wider than the list
   * rather than wrong. An Owner rule is the exception: it names the column the
   * map's own salesRep parameter holds, so it crosses over as that.
   */
  private syncMapFilter(): void {
    if (this.scopedFetch) return;
    const filter: LeadMapFilter = {
      column: this.state.columns,
      // "Assigned to me" and an Owner rule narrow the same column, and
      // filterParams() drops the rule while the chip is on. The map is told
      // the same thing, so the two sides cannot disagree about whose leads
      // these are.
      salesRep: this.state.assignedToMeOnly
        ? this.myUserId
        : ruleUserId(this.state.ruleFilters['salesRep']),
      search: this.state.search.trim(),
      territoryId: this.state.territoryId,
      territoryOwner: this.activeTerritoryOwner,
      highContributionOnly: this.state.highContributionOnly,
    };
    const key = JSON.stringify(filter);
    // load() runs on paging and on a sort too; every tile is refetched behind
    // this call, so only a changed filter may reach the layer.
    if (key === this.mapFilterKey) return;
    this.mapFilterKey = key;
    this.leadLayer?.filter(filter);
  }

  /**
   * The compact list owns its own search box and reloads itself from it, so
   * this is not another reload - it is the map being told what the rows were
   * already narrowed by. Debounced upstream, so it is one settled search
   * rather than one per keystroke.
   */
  onCompactSearch(search: string): void {
    this.state.search = search;
    this.syncMapFilter();
  }

  /** A rule was added, changed or removed on the filter bar. */
  onRuleFilterChange(values: DataFilterValues): void {
    this.state.ruleFilters = values;
    this.onServerFilterChange();
    this.onClientFilterChange();
  }

  /** Apply replaces only the rule-filter slice. Search, territory, stage focus,
   * Assigned to me, and every Board column sort remain person-owned controls. */
  private applyAgentTableView(
    action: LeadTableViewAction,
  ): SurfaceActionApplyResult | null {
    const filters = this.agentRuleFilters(action);
    if (!filters) return null;

    const isList = this.state.view === 'list';
    const table = isList ? this.listTable : undefined;
    const sortField = action.listSort?.field ?? '';
    if (isList && (!table || !table.canSetServerSort(sortField))) return null;

    const snapshot: LeadTableViewSnapshot = {
      ruleFilters: cloneFilterValues(this.state.ruleFilters),
      ...(table ? { listSort: table.getServerSort() } : {}),
    };
    this.state.ruleFilters = filters;
    this.page = 1;

    if (table) {
      // Facets share the new filters. The table API performs the only List-row
      // fetch, updates the header, and resets paging in one operation.
      this.load(false, false);
      table.setServerSort(sortField, action.listSort?.direction === 'desc');
    } else {
      // Board does not borrow List sorting; its independent per-column sorts
      // remain unchanged.
      this.load(false, true);
    }

    return {
      restoreState: snapshot,
      note: !isList && action.listSort
        ? 'Filters applied. List sort is not applied on Board; Board column sorts are unchanged.'
        : undefined,
    };
  }

  private restoreAgentTableView(snapshot: unknown): boolean {
    if (!isLeadTableViewSnapshot(snapshot)) return false;
    const table = this.state.view === 'list' ? this.listTable : undefined;
    if (this.state.view === 'list'
        && (!table || !snapshot.listSort
          || !table.canSetServerSort(snapshot.listSort.field))) {
      return false;
    }

    this.state.ruleFilters = cloneFilterValues(snapshot.ruleFilters);
    this.page = 1;
    if (table && snapshot.listSort) {
      this.load(false, false);
      table.setServerSort(snapshot.listSort.field, snapshot.listSort.desc);
    } else {
      this.load(false, true);
    }
    return true;
  }

  /** Final target-side narrowing: the field must still be on this mounted
   * page and a choice must still exist in the tenant's current catalog. */
  private agentRuleFilters(action: LeadTableViewAction): DataFilterValues | null {
    const filters: Record<string, DataFilterValues[string]> = {};
    for (const rule of action.rules) {
      const field = this.filterFields.find(candidate => candidate.id === rule.field);
      if (!field) return null;
      const kind = field.kind ?? 'choice';
      if (!DATA_FILTER_OPS[kind].some(operator => operator.id === rule.op)) return null;

      if (kind === 'choice') {
        const option = field.options.find(candidate => String(candidate.value) === rule.value);
        if (!option) return null;
        filters[rule.field] = option.value;
      } else if (kind === 'number') {
        const value = Number(rule.value);
        if (!Number.isFinite(value)) return null;
        filters[rule.field] = { op: rule.op, value };
      } else {
        filters[rule.field] = { op: rule.op, value: rule.value };
      }
    }
    return filters;
  }

  load(reloadList = true, loadBoardItems = true): void {
    if (this.scopedFetch) {
      if (reloadList) { this.listTable?.reload(); this.compactList?.reload(); }
      return;
    }
    this.loadError = null;
    this.syncMapFilter();
    const seq = ++this.loadSeq;   // only the newest load may apply its results
    const params: Record<string, string | string[]> = {
      ...this.filterParams(),
      page: String(this.page), pageSize: String(this.pageSize),
    };

    // The rail counts the same set the rows come from, minus the stage - which
    // is the one thing its own columns are for. Filtered any narrower and every
    // column but the chosen one would read zero; any wider and the rail would
    // describe a set the list below it is not showing.
    const facetParams = { ...this.filterParams() };
    delete facetParams['column'];
    this.readCollection<FacetsResponse>(facetParams, true).subscribe({
      next: res => {
        if (seq !== this.loadSeq) return;
        this.facets = res.columns;
        this.totalCount = res.totalCount;
      },
      error: () => { /* the items call reports the page-level error */ },
    });
    if (loadBoardItems && this.state.view === 'board') {
      for (const page of Object.values(this.boardPages)) page.request?.unsubscribe();
      for (const column of Object.keys(this.boardPages)) delete this.boardPages[column];
      this.items = [];
      for (const column of this.visibleColumns) this.resetBoardColumn(column);
    } else if (loadBoardItems) {
      this.readCollection<ListResponse>(params).subscribe({
        next: res => {
          if (seq !== this.loadSeq) return;   // a later filter already superseded this
          this.items = res.items;
          this.listTotal = res.total;
        },
        error: () => {
          if (seq !== this.loadSeq) return;
          this.loadError = 'Failed to load work items.';
        },
      });
    }
    // The List's table owns its own page and sort; a filter change re-queries
    // it with the state this fetch reads at call time. Absent before the first
    // render of the list view — its own init load covers that case.
    if (reloadList) {
      this.listTable?.reload();
      this.compactList?.reload();
    }
  }

  /** True when more rows exist than the loaded page (06 §5.5: never claim a
   * full scope silently). The List pages through them; Board shows the
   * current page's lens. */
  get truncated(): boolean { return this.listTotal > this.items.length; }

  // ── Server paging: the List is the full-data surface ───────────────────

  get pageCount(): number { return Math.max(1, Math.ceil(this.listTotal / this.pageSize)); }

  prevPage(): void { if (this.page > 1) { this.page--; this.load(); } }
  nextPage(): void { if (this.page < this.pageCount) { this.page++; this.load(); } }

  /** Any server-side filter change restarts at page 1. */
  // -- What the map asks for ---------------------------------------------
  //
  // A pin's popover hands its lead back, which is the mirror of a row asking
  // the map to show one. The list finds the row and marks it. If it cannot,
  // the only things that can hide a lead the map is drawing are the rules the
  // map was never told about - so those come off, and both halves of the
  // screen show the same leads. What came off is said, and one click puts it
  // back.

  private revealRequests?: Subscription;

  /** The lead asked for, and whether its filters have been widened already. */
  private pendingReveal: { id: number; widened: boolean } | null = null;

  /** The line under the filter bar, and what Undo would put back. */
  revealNotice: string | null = null;
  private revealRestore: DataFilterValues | null = null;

  get canRestoreRevealFilters(): boolean { return !!this.revealRestore; }

  /**
   * The rules syncMapFilter passes on, and therefore the ones that may stay.
   *
   * Everything else on the bar narrows this list alone, which is exactly why
   * a pin can exist for a lead the list is hiding. Stage, territory, search
   * and the contribution floor are not here because they are not rules - they
   * are their own controls, and they already cross over.
   */
  private static readonly RulesTheMapShares: readonly string[] = ['salesRep'];

  private revealFromMap(objectId: number | string): void {
    const id = Number(objectId);
    if (!Number.isFinite(id)) return;
    this.pendingReveal = { id, widened: false };
    this.revealNotice = null;
    this.compactList?.reveal(row => row.leadId === id);
  }

  /** The list read every page it had and still did not find it. */
  onCompactRevealMissed(): void {
    const pending = this.pendingReveal;
    if (!pending) return;
    const dropped = pending.widened ? [] : this.dropRulesTheMapDoesNotShare();
    if (!dropped.length) {
      this.pendingReveal = null;
      this.revealNotice = 'That lead is not in this list.';
      return;
    }
    pending.widened = true;
    this.revealNotice =
      `Cleared ${dropped.join(' and ')} so this list shows what the map shows.`;
    // Dropping the rules re-read the rows; the list is still looking for the
    // same lead, so it finds it as the new pages land.
    this.compactList?.reveal(row => row.leadId === pending.id);
  }

  /** Puts back what was cleared to show that lead. */
  restoreRevealedFilters(): void {
    const restore = this.revealRestore;
    if (!restore) return;
    this.revealRestore = null;
    this.revealNotice = null;
    this.pendingReveal = null;
    this.onRuleFilterChange(restore);
  }

  /** Returns what it cleared, named the way the filter bar names it. */
  private dropRulesTheMapDoesNotShare(): string[] {
    const values = this.state.ruleFilters;
    const dropped = Object.keys(values).filter(id =>
      !LeadsBoardComponent.RulesTheMapShares.includes(id)
      && hasRuleValue(values[id]));
    if (!dropped.length) return [];

    this.revealRestore = cloneFilterValues(values);
    const kept: Record<string, DataFilterValue | DataFilterRule> = {};
    for (const id of Object.keys(values)) {
      if (!dropped.includes(id)) kept[id] = values[id];
    }
    this.onRuleFilterChange(kept);
    return dropped.map(id =>
      this.filterFields.find(field => field.id === id)?.label ?? id);
  }

  onServerFilterChange(): void {
    this.page = 1;
    this.load();
  }

  /** The contribution filter changed. The Board prunes its loaded rows, but
   * the List is paged in the database, so its table re-queries. */
  onClientFilterChange(): void {
    this.listTable?.reload();
    this.compactList?.reload();
  }

  get visibleColumns(): string[] {
    return this.leadStages.map(stage => stage.code)
      .filter(code => !this.state.columns.length || this.state.columns.includes(code));
  }

  clearColumn(): void {
    this.state.columns = [];
    this.onStageFilterChange();
  }

  // ── Milestone rail (the CRM customer rail, ported to the pipeline) ──────

  /** Every active stage in the tenant's configured order, including outcomes. */
  get railStages(): string[] {
    return this.leadStages.map(stage => stage.code);
  }

  /**
   * The stages as the shared rail wants them.
   *
   * Rebuilt per change-detection pass, which is what a getter feeding an
   * @Input costs - acceptable here because the rail is a handful of stages and
   * the alternative is a cache to invalidate on every facet reply.
   */
  get railStops(): MilestoneStop[] {
    return this.leadStages.map(stage => ({
      id: stage.code,
      label: stage.name ?? stage.code,
      count: this.railCount(stage.code),
      color: stageColor(stage.code),
    }));
  }

  /** Server-side facet count for a stage - the rail reports the whole filter
   *  context, not just the loaded page. */
  railCount(stage: string): number {
    return this.facetFor(stage)?.count ?? 0;
  }

  stageColor(stage: string): string {
    return stageColor(stage);
  }

  get stageFilterLabel(): string {
    if (!this.state.columns.length) return 'All stages';
    return this.state.columns.length === 1
      ? this.columnLabel(this.state.columns[0])
      : `${this.state.columns.length} stages selected`;
  }

  /** Each stage toggles independently; All clears the restriction. */
  selectStage(stage: string | null): void {
    const selected = new Set(this.state.columns);
    if (stage === null) selected.clear();
    else if (selected.has(stage)) selected.delete(stage);
    else selected.add(stage);
    this.state.columns = this.leadStages.map(row => row.code).filter(code => selected.has(code));
    this.onStageFilterChange();
  }

  /** The name remains the keyboard action; card background clicks share it.
   * Map controls and stage/assignment recovery panels keep their own actions.
   * A new pointer gesture clears suppression of the previous drag's click. */
  boardDragClick = false;
  onBoardCardClick(event: MouseEvent, item: WorkItem): void {
    if (this.boardDragClick) return;
    const target = event.target;
    if (!(target instanceof Element) || target.closest('button, a, input, select, textarea, app-map-pin, .rb-move-panel')) return;
    const card = event.currentTarget as HTMLElement;
    card.querySelector<HTMLButtonElement>('.lead-summary__name')?.focus({ preventScroll: true });
    this.openDetail(item);
  }

  /** Open the routed lead page; board/list never create another overlay. */
  openDetail(item: WorkItem, event?: MouseEvent): void {
    if (event && event.detail !== 0 && this.boardDragClick) return;
    if (this.originLabel) {
      this.router?.navigate(['/leads', item.leadId], { state: this.workspaceNavigation?.sourceState(this.originLabel) });
    } else this.router?.navigate(['/leads', this.collectionRoute, item.leadId]);
  }

  /** Open the form inside this collection route so Cancel returns to it. */
  newLead(): void {
    this.router?.navigate(['/leads', this.state.view === 'board' ? 'board' : 'table', 'new']);
  }

  editLead(item: WorkItem): void {
    this.router?.navigate(['/leads', this.collectionRoute, item.leadId, 'edit']);
  }

  private get collectionRoute(): 'table' | 'board' {
    return this.state.view === 'list' ? 'table' : 'board';
  }

  /** Stage select: server-side filter + selection prune for List and Board. */
  onStageFilterChange(): void {
    this.onServerFilterChange();
  }

  private get filteredItems(): WorkItem[] {
    let rows = this.items;
    if (this.state.columns.length) rows = rows.filter(i => this.state.columns.includes(i.boardColumnCode));
    if (this.state.highContributionOnly)
      rows = rows.filter(i =>
        (i.contributionBase ?? 0) >= LeadFeature.MinAnnualContribution);
    return rows;
  }

  // ── Row grouping (06 §5.6) ─────────────────────────────────────────────

  /** A rep's display name, or the id the card carries when the roster is not
   *  readable. "Owner N" is the Board's own vocabulary for that fallback. */
  repLabel(userId: number, fallback = `Owner ${userId}`): string {
    const rep = this.reps.find(r => r.userId === userId);
    return rep?.displayName?.trim() || rep?.userName?.trim() || fallback;
  }

  repInitials(userId: number): string | null {
    const name = this.repLabel(userId, '').split('@')[0].trim();
    if (!name) return null;
    const words = name.split(/\s+/);
    return (words.length > 1 ? words[0][0] + words[words.length - 1][0] : name.slice(0, 2)).toUpperCase();
  }

  private ownerRow(userId: number | null): BoardRow {
    return userId != null
      ? { key: `o${userId}`, label: this.repLabel(userId), salesRepUserId: userId }
      : { key: 'o-none', label: 'Unassigned', salesRepUserId: null };
  }

  private rowFor(item: WorkItem): BoardRow {
    switch (this.state.rowGroup) {
      case 'salesRep':
        return this.ownerRow(item.salesRepUserId);
      case 'routeFit':
        return { key: `r${item.routeFitStatus ?? 'none'}`, label: item.routeFitStatus ?? 'No route fit' };
      case 'contributionTier': {
        const value = item.contributionBase;
        if (value == null) return { key: 'p-unknown', label: 'Contribution unknown' };
        return value >= LeadFeature.MinAnnualContribution
          ? { key: 'p-high', label: 'Annual contribution ≥ $10k' }
          : { key: 'p-std', label: 'Annual contribution < $10k' };
      }
      default:
        return { key: 'all', label: '' };
    }
  }

  get rows(): BoardRow[] {
    if (this.state.rowGroup === 'none') return [{ key: 'all', label: '' }];
    const seen = new Map<string, BoardRow>();
    // The assignment view (3c) rows by rep, so EVERY rep gets a row even with
    // nothing in it — an idle rep you cannot drop onto is not an assignment
    // view. The other groupings still derive their rows from the cards.
    if (this.state.rowGroup === 'salesRep')
      for (const rep of this.reps) seen.set(`o${rep.userId}`, this.ownerRow(rep.userId));
    for (const item of this.filteredItems) {
      const row = this.rowFor(item);
      if (!seen.has(row.key)) seen.set(row.key, row);
    }
    // Unassigned last: an ownerless opportunity is an exception, not a peer of
    // the reps (3c §44) — it reads as the flat row under them.
    const rows = [...seen.values()].sort((a, b) => a.label.localeCompare(b.label));
    const unassigned = rows.filter(r => r.key === 'o-none');
    return [...rows.filter(r => r.key !== 'o-none'), ...unassigned];
  }

  /** Cards in one row, across every visible column — that row's own total. */
  private rowItems(rowKey: string): WorkItem[] {
    if (rowKey === 'all') return this.filteredItems;
    return this.filteredItems.filter(i => this.rowFor(i).key === rowKey);
  }

  /** "N in play · $X" for a rep row. Deliberately computed from the cards the
   *  row actually renders, so the summary can never disagree with them (the
   *  Board is one server page's lens; the facets are the whole-filter figure). */
  rowSummary(row: BoardRow): string {
    const rows = this.rowItems(row.key);
    const total = rows.reduce((sum, r) => sum + (r.contributionBase ?? 0), 0);
    const money = total >= 1000 ? `$${Math.round(total / 1000)}k` : `$${Math.round(total)}`;
    return `${rows.length} in play · ${money}`;
  }

  itemsFor(column: string, rowKey: string): WorkItem[] {
    const rows = this.filteredItems
      .filter(i => i.boardColumnCode === column && (rowKey === 'all' || this.rowFor(i).key === rowKey));
    return LeadsBoardComponent.sorted(rows, this.state.sortFor(column));
  }

  /**
   * One column's cards in the order it is set to.
   *
   * A copy, never a sort in place: `items` is the board's single array and
   * every column reads it, so sorting it for one column would reorder the
   * others - and would fight the drop position that `applyLocalMove` just
   * wrote into it.
   *
   * `manual` returns the array order untouched, which is what makes a dropped
   * card stay where it was dropped. Every other rule is an explicit request to
   * overrule that arrangement, and a column under one will move a dropped card
   * to wherever the rule puts it - the sort is the answer to "where does this
   * go" for as long as it is on.
   *
   * Missing values sort LAST under every rule. A card with no contribution
   * figure is not the cheapest one and a card with no next action is not the
   * most urgent; putting absent where zero would go states something the data
   * does not.
   */
  static sorted(rows: WorkItem[], sort: ColumnSort): WorkItem[] {
    if (sort === 'manual') return rows;
    const last = (value: number | null) => value ?? Number.POSITIVE_INFINITY;
    const compare: Record<Exclude<ColumnSort, 'manual'>, (a: WorkItem, b: WorkItem) => number> = {
      // Negated so the biggest is first while absent still sorts last.
      value: (a, b) => last(a.contributionBase == null ? null : -a.contributionBase)
        - last(b.contributionBase == null ? null : -b.contributionBase),
      age: (a, b) => last(stamp(a.stageEnteredAt)) - last(stamp(b.stageEnteredAt)),
      due: (a, b) => last(stamp(a.nextActionDue)) - last(stamp(b.nextActionDue)),
      title: (a, b) => (a.title ?? '').localeCompare(b.title ?? ''),
    };
    // A copy first, because Array.prototype.sort works in place.
    return [...rows].sort(compare[sort]);
  }

  hasMoreInColumn(column: string): boolean {
    const page = this.boardPages[column];
    return !!page && (page.page === 0 || page.dirty || page.page * this.boardPageSize < page.total);
  }

  setColumnSort(column: string, sort: ColumnSort): void {
    this.state.setSortFor(column, sort);
    this.resetBoardColumn(column);
  }

  private resetBoardColumn(column: string): void {
    this.boardPages[column]?.request?.unsubscribe();
    this.boardPages[column] = { page: 0, total: 0, loading: false, error: '', dirty: false };
    this.items = this.items.filter(item => item.boardColumnCode !== column);
    this.loadMoreInColumn(column);
  }

  loadMoreInColumn(column: string): void {
    const state = this.boardPages[column];
    if (!state || state.loading || this.boardMovesPending) return;
    if (state.page && !this.hasMoreInColumn(column)) return;
    const nextPage = state.page + 1;
    const refresh = state.dirty;
    const params: Record<string, string | string[]> = { ...this.filterParams(), column,
      pageSize: String(this.boardPageSize) };
    if (this.state.highContributionOnly) params['minContribution'] = String(LeadFeature.MinAnnualContribution);
    const sort = this.state.sortFor(column);
    if (sort !== 'manual') {
      params['sort'] = { value: 'contributionBase', age: 'stageEnteredAt', due: 'nextActionDue', title: 'title' }[sort];
      params['desc'] = String(sort === 'value');
    }
    // Moves shift offset boundaries. Re-read the loaded range before extending it,
    // preserving existing card identities and manual positions, instead of skipping a lead.
    const pages = refresh ? Array.from({ length: nextPage }, (_, i) => i + 1) : [nextPage];
    state.loading = true;
    state.error = '';
    state.request = forkJoin(pages.map(page => this.readCollection<ListResponse>({ ...params, page: String(page) })))
      .subscribe({
        next: results => {
          if (this.boardPages[column] !== state) return;
          state.loading = false;
          const last = results[results.length - 1];
          if (!last.items.length && (nextPage - 1) * this.boardPageSize < last.total) {
            state.error = 'The result set changed. Retry to load more leads.';
            return;
          }
          const incoming = new Map(results.flatMap(result => result.items).map(item => [item.leadId, item]));
          const retained = this.items.filter(item => item.boardColumnCode !== column || !refresh || incoming.has(item.leadId));
          for (const item of retained) {
            const update = incoming.get(item.leadId);
            if (update) { Object.assign(item, update); incoming.delete(item.leadId); }
          }
          this.items = [...retained, ...incoming.values()];
          state.page = nextPage;
          state.total = last.total;
          state.dirty = false;
        },
        error: () => {
          if (this.boardPages[column] !== state) return;
          state.loading = false;
          state.error = state.page ? 'Unable to load more leads. Try again.' : 'Unable to load leads. Try again.';
        },
      });
  }

  private invalidateBoardColumns(...columns: string[]): void {
    for (const column of columns) {
      const page = this.boardPages[column];
      if (!page) continue;
      page.request?.unsubscribe();
      page.loading = false;
      page.dirty = true;
    }
  }

  facetFor(column: string): Facet | undefined {
    return this.facets.find(f => f.column === column);
  }

  /** The rules a column may be arranged by, for its menu. */
  readonly columnSorts = COLUMN_SORTS;

  /** What this column is sorted by, in words. */
  sortLabel(column: string): string {
    const sort = this.state.sortFor(column);
    return COLUMN_SORTS.find(option => option.id === sort)?.label ?? '';
  }

  /** The control says what it is currently doing, not just what it opens - a
   *  bare "Sort" gives no sign that this column is already under a rule. */
  sortTooltip(column: string): string {
    return this.state.sortFor(column) === 'manual'
      ? 'Sort this column'
      : `Sorted by ${this.sortLabel(column).toLowerCase()}`;
  }

  // ── Stage-change preview: Move button, Enter, or drag ─────────

  startMove(item: WorkItem): void {
    if (!this.availableTargets(item).length) return;
    const same = this.moveTarget?.leadId === item.leadId;
    this.cancelAssign();
    this.moveTarget = same ? null : item;
    this.moveError = null;
    this.moveNote = '';
  }

  /** Dismiss the move panel without moving anything. */
  cancelMove(): void {
    this.moveTarget = null;
    this.moveError = null;
    this.moveNote = '';
  }

  /** Enter opens the preview only when the card itself is focused - a keyup
   * bubbling up from the note input or a Confirm/target button inside the
   * panel must NOT re-trigger startMove (which would close the panel and
   * swallow the just-shown transition error). */
  onCardEnter(event: Event, item: WorkItem): void {
    if (event.target === event.currentTarget) this.startMove(item);
  }

  /**
   * A drop onto a legal column IS the move, and the card goes where it was
   * dropped, at once.
   *
   * Dragging a card is already a deliberate act aimed at a named column, so
   * asking "Move to X?" afterwards made every move two gestures for an answer
   * the drag had given. The keyboard action keeps its panel - there the panel is
   * how a target gets chosen, not a second opinion about one.
   *
   * The card used to stay put until the command returned AND a full two-request
   * reload had redrawn the board. Between the drop and the redraw the board
   * showed the card in the column it came from, which is indistinguishable
   * from "nothing happened" - so the gesture read as broken, and people
   * dragged again. It now moves immediately, into the slot the drop aimed at,
   * and rolls back to exactly where it was if the server refuses.
   *
   * An illegal target still opens the panel rather than committing, because
   * that is a refusal and not a confirmation: dropping a card somewhere it
   * cannot go has to say so, or the gesture silently does nothing.
   */
  onDrop(event: CdkDragDrop<string>, targetColumn: string, targetRow = 'all'): void {
    const item = event.item.data as WorkItem;
    // Assignment view (3c): a drop into ANOTHER rep's row is a reassignment,
    // not a stage move — the row is what the user aimed at. It reuses this one
    // cdkDrag wiring; there is no second drag mechanism on the Board.
    if (this.state.rowGroup === 'salesRep' && targetRow !== 'all'
        && targetRow !== this.rowFor(item).key) {
      this.startAssign(item, this.rows.find(r => r.key === targetRow) ?? null);
      return;
    }
    // Dropped back into the column it came from. Not a stage change, so no
    // command and nothing to take back - but it IS an arrangement this person
    // just made with their own hands, which is exactly what `manual` exists to
    // keep. This used to `return` here, so a card dragged within its own
    // column snapped back to where it started, and the sort's promise that a
    // dropped card stays where it was dropped held only for cards that had
    // arrived from some other column.
    if (item.boardColumnCode === targetColumn) {
      this.reorderWithinColumn(
        item, targetColumn, this.slotDroppedOn(event), targetRow);
      return;
    }
    this.cancelAssign();
    this.moveNote = '';
    const allowed = this.availableTargets(item);
    if (allowed.includes(targetColumn))
    {
      // Legal: the drag said it. No panel, no note - a note is offered by the
      // keyboard panel and the drawer, which are the surfaces where someone has
      // stopped to type.
      this.moveTarget = null;
      this.moveError = null;
      this.confirmMove(item, targetColumn, this.slotDroppedOn(event), targetRow);
      return;
    }

    // Illegal target: don't fire a command that will 409 - open the panel on
    // the legal-targets list with an explanation.
    this.moveTarget = item;
    this.moveError =
      `Can't move ${item.boardColumn} to ${this.columnLabel(targetColumn)}.`;
  }

  /**
   * Put a card back into its own column, in the slot it was let go over.
   *
   * Local only, and deliberately so: nothing about the lead has changed, so
   * there is no command to send, no snackbar to raise and nothing to add to
   * the undo journal. The order lives in `items` in exactly the way a
   * cross-column drop's landing slot does, and is given up by the next reload
   * for exactly the same reason - the server holds no opinion about where
   * inside a stage a card sits.
   *
   * A column under any rule but `manual` is left alone. Its order is already
   * the answer to "where does this go", its header says so out loud, and
   * re-splicing the array would be overruled by `sorted()` on the very next
   * render anyway.
   */
  private reorderWithinColumn(
    item: WorkItem, column: string, dropIndex: number | null, rowKey: string,
  ): void {
    if (dropIndex == null) return;
    if (this.state.sortFor(column) !== 'manual') return;
    // Its own row only: a drop into another row's copy of this column is
    // aimed at that row, and those are not the slots this card sits among.
    if (rowKey !== 'all' && rowKey !== this.rowFor(item).key) return;

    // Out first, so the slots are counted without it - the same reason
    // `applyLocalMove` does it, and what keeps a card dragged downwards inside
    // one column from anchoring on itself.
    const items = this.items.filter(row => row.leadId !== item.leadId);
    this.items = items;
    const rendered = this.itemsFor(column, rowKey);
    const anchor = rendered[dropIndex];
    const at = anchor
      ? items.indexOf(anchor)
      // Past the last card: after the column's last one, not at the end of the
      // whole board array, which is some other column's territory.
      : rendered.length ? items.indexOf(rendered[rendered.length - 1]) + 1
        : items.length;
    items.splice(at, 0, item);
    this.items = items;
  }

  /**
   * Which slot in the target column the card was let go over.
   *
   * Measured from the pointer against the cards actually on screen, rather
   * than taken from `event.currentIndex`. The CDK index is derived from a
   * cache of placeholder positions maintained during the drag, and in this
   * board it kept coming back 0, so every drop landed at the top of the
   * column whatever it was aimed at. Reading the rectangles at the moment of
   * the drop cannot disagree with what the person saw: the answer is a
   * property of the screen, not of anyone's bookkeeping.
   *
   * Falls back to the CDK index when there is no pointer or no host to
   * measure - which is every spec that fabricates a drop event, and would be
   * any future caller that has a slot in mind but no mouse.
   */
  private slotDroppedOn(event: CdkDragDrop<string>): number | null {
    const y = event.dropPoint?.y;
    const host = event.container?.element?.nativeElement as HTMLElement | undefined;
    if (y == null || !host?.querySelectorAll) return event.currentIndex ?? null;
    // The placeholder is a CLONE of the dragged card and carries `.rb-card`
    // too; counting it would shift every slot below the pointer by one.
    const cards = Array.from(host.querySelectorAll('.rb-card'))
      .filter(el => !el.classList.contains('cdk-drag-placeholder'));
    return LeadsBoardComponent.slotAt(
      cards.map(el => el.getBoundingClientRect()), y);
  }

  /**
   * The slot a pointer at `y` is asking for, among cards laid out top to
   * bottom: before the first card whose middle it has not yet passed, and
   * after all of them when it has passed every one.
   *
   * Midpoints rather than edges, because the gap between two cards has to
   * belong to one of them - splitting on the boundary leaves a few pixels
   * where a drop means nothing.
   */
  static slotAt(cards: { top: number; height: number }[], y: number): number {
    const at = cards.findIndex(rect => y < rect.top + rect.height / 2);
    return at === -1 ? cards.length : at;
  }

  /**
   * Redraw the card in its new column NOW, and hand back the way to put
   * everything back exactly as it was.
   *
   * A rollback closure rather than a re-fetch, because the thing being undone
   * is a local edit: re-fetching to recover from a refusal would reintroduce
   * the very wait this exists to remove, and would also discard any other
   * drop made in the meantime.
   *
   * `dropIndex` is the slot the drop aimed at within the target column, or
   * null from the Move button, which aims at a column and not at a position.
   * Anchoring on the card CURRENTLY at that slot, rather than on a numeric
   * index into `items`, is what makes this survive the filtering and the
   * column sorting between the array and what the column renders.
   */
  private applyLocalMove(
    item: WorkItem, toCode: string, dropIndex: number | null, rowKey: string,
  ): () => void {
    const stage = this.leadStages.find(s => s.code === toCode);
    const before = {
      items: [...this.items],
      facets: this.facets.map(facet => ({ ...facet })),
      boardColumn: item.boardColumn,
      boardColumnCode: item.boardColumnCode,
      boardColumnOutcome: item.boardColumnOutcome,
      stageEnteredAt: item.stageEnteredAt,
    };
    const fromCode = item.boardColumnCode;
    this.invalidateBoardColumns(fromCode, toCode);

    // Out first, so the target column's slots are counted without it - a card
    // dragged down within a board that also filters would otherwise anchor on
    // itself.
    const items = this.items.filter(row => row.leadId !== item.leadId);

    item.boardColumnCode = toCode;
    item.boardColumn = stage?.name ?? toCode;
    if (stage) item.boardColumnOutcome = stage.outcome;
    // "0d in stage" from this second, not the age it carried out of the last
    // column - that number is the whole point of the badge.
    item.stageEnteredAt = new Date().toISOString();

    // Read the target column AS RENDERED, with the card already out of the
    // board, so the slot counted here is the slot the person was looking at.
    // Filtering and a column's own sort sit between
    // `items` and what a column shows, and an index into one is not an index
    // into the other.
    this.items = items;
    const column = this.itemsFor(toCode, rowKey);
    const anchor = dropIndex == null ? undefined : column[dropIndex];
    const at = anchor
      ? items.indexOf(anchor)
      // Past the last card, or no position given: after the column's last
      // card, so it lands at the bottom of that column rather than at the
      // bottom of some other one.
      : column.length ? items.indexOf(column[column.length - 1]) + 1 : items.length;
    items.splice(at, 0, item);
    this.items = items;

    this.retally(fromCode, -1, item.contributionBase ?? 0);
    this.retally(toCode, 1, item.contributionBase ?? 0);

    return () => {
      this.items = before.items;
      this.facets = before.facets;
      item.boardColumn = before.boardColumn;
      item.boardColumnCode = before.boardColumnCode;
      item.boardColumnOutcome = before.boardColumnOutcome;
      item.stageEnteredAt = before.stageEnteredAt;
    };
  }

  /**
   * Keep a column header honest about a card that just arrived or left.
   *
   * The headers are whole-filter figures from the server, so a local move that
   * did not touch them would leave every header stating a tally that no longer
   * matches the board under it. `share` is recomputed for EVERY column because
   * the server defines it as count/totalCount - a denominator that does not
   * change here, but two numerators that do.
   */
  private retally(column: string, byCount: number, byContribution: number): void {
    const facet = this.facets.find(f => f.column === column);
    if (!facet) return;   // a stage the facets never carried; nothing to correct
    facet.count = Math.max(0, facet.count + byCount);
    facet.contribution = Math.max(0, facet.contribution + byCount * byContribution);
    if (this.totalCount > 0)
      this.facets.forEach(f => { f.share = f.count / this.totalCount; });
  }

  /** Where this card may be dropped, by the same rule the server enforces and
   *  the same one the drawer's rail reads. */
  availableTargets(item: WorkItem): string[] {
    return legalMoveCodes(this.leadStages, this.stageTransitions, item.boardColumnCode);
  }

  updateCompactLead(row: WorkItem, profile: LeadProfile): void {
    row.title = profile.name;
    row.address = profile.addressLine1;
    row.lat = profile.latitude;
    row.lng = profile.longitude;
    row.city = profile.city;
    row.state = profile.state;
    row.postalCode = profile.postalCode;
    row.businessTypes = profile.businessTypes.map(type => type.name);
    row.nextActionType = profile.nextActionType;
    row.nextActionDue = profile.nextActionDue;
  }

  readonly rowId = (row: WorkItem): number => row.leadId;
  trackByLead = (_: number, item: WorkItem): number => item.leadId;
  trackByRow = (_: number, row: BoardRow): string => row.key;

  // ── Assignment preview: rows by rep, drag to reassign (design 3c) ──────
  //
  // Reassignment writes the Lead directly. The server validates tenancy and
  // that the owner belongs to the company.
  // The drop does NOT mutate any row array, so the card is still under the
  // original rep while the panel is open and a failure leaves it exactly where
  // it was — there is no optimistic move to roll back.

  /** Open the confirmation for "assign this card to that row's rep". */
  startAssign(item: WorkItem, row: BoardRow | null): void {
    this.moveTarget = null;
    this.moveError = null;
    this.assignTarget = item;
    this.assignTo = row;
    this.assignError = null;
    if (row == null || row.salesRepUserId == null) {
      this.assignTo = null;
      this.assignError = 'An owner cannot be removed here - drop the card on a rep instead.';
    }
  }

  cancelAssign(): void {
    this.assignTarget = null;
    this.assignTo = null;
    this.assignError = null;
  }

  confirmAssign(item: WorkItem, row: BoardRow): void {
    if (row.salesRepUserId == null) return;
    this.assignError = null;
    this.assigning = true;
    this.http.put(`/api/lead-pipeline/${item.leadId}`, { salesRepUserId: row.salesRepUserId }).subscribe({
      next: () => {
        this.assigning = false;
        this.assignTarget = null;
        this.assignTo = null;
        this.load();          // the reassigned card reappears under its new row
      },
      error: err => {
        this.assigning = false;
        // Nothing moved, so there is nothing to undo — only a reason to show.
        this.assignError = err.error?.error
          ?? (err.status === 403 ? 'You do not have permission to reassign leads.'
            : err.status === 404 ? 'That rep is not on this account, or the lead no longer exists.'
              : 'Reassignment failed.');
      },
    });
  }

  confirmMove(
    item: WorkItem, to: string,
    dropIndex: number | null = null, rowKey = 'all',
  ): void {
    const leadStageId = this.stageId(to);
    if (leadStageId == null) return;
    // Captured before anything moves: the retag below overwrites the very
    // fields an undo needs to name where this card came from.
    const step: UndoableMove = {
      leadId: item.leadId,
      title: item.title ?? `Lead ${item.leadId}`,
      fromCode: item.boardColumnCode,
      toCode: to,
      // Where it sat before it was picked up, read from the column AS
      // RENDERED - a sorted column shows a different order from the array,
      // and the slot to give back is the one the person was looking at.
      fromIndex: this.itemsFor(item.boardColumnCode, this.rowFor(item).key)
        .findIndex(row => row.leadId === item.leadId),
    };
    this.moveError = null;
    this.boardMovesPending++;
    const rollback = this.applyLocalMove(item, to, dropIndex, rowKey);
    this.http.put(`/api/lead-pipeline/${item.leadId}/stage`,
      { leadStageId, note: this.moveNote || null })
      .pipe(finalize(() => this.boardMovesPending--)).subscribe({
      next: () => {
        this.moveTarget = null;
        this.state.undo.record(step);
        // Deliberately no reload. The board already shows the move, and
        // redrawing from the server would drop the card wherever the server's
        // ordering puts it - taking back the one thing the drop just decided.
        // Refresh, a filter change and Undo all still reload.
        // The same offer the button makes, at the moment of the mistake -
        // which is when a person knows it was one.
        // Longer than a notice needs, because it is also the offer - but
        // still finite: a snackbar sits over the board it is talking about.
        // The Undo button on the rail is what makes the offer lasting, and it
        // does not time out at all.
        this.snackBar?.open(
          `Moved ${step.title} to ${this.columnLabel(to)}`, 'Undo',
          { duration: 12000 })
          .onAction().subscribe(() => this.undoLastMove());
      },
      error: err => {
        // Put it back where it was - not by reloading, which would reintroduce
        // the wait this move exists to avoid and would discard any other drop
        // made in the meantime.
        rollback();
        // The panel is where a reason is rendered, and a dragged move has none
        // open - so a refusal opens one on the card it refused.
        this.moveTarget = item;
        this.moveError = err.error?.error
          ?? (err.status === 403 ? 'You do not have permission to move leads.' : 'Stage change failed.');
      },
    });
  }

  // ── Undo: walking the move journal backwards ──────────────────────────
  //
  // An undo is a COMPENSATING MOVE, not an erasure. It issues the same stage
  // command back the other way, with a note saying what it is, so the lead's
  // history keeps both steps and stays an honest record of what happened.
  // Nothing here asks the server to forget.

  /** In-flight guard: one retraction at a time, in order. */
  undoing = false;

  get canUndo(): boolean { return this.state.undo.canUndo; }

  /** How many moves are still waiting to be taken back. Printed on the button
   *  so the offer is visible from across the page and obviously still open -
   *  a lone "Undo" says nothing about whether there is anything left to undo. */
  get undoCount(): number { return this.state.undo.count; }

  /** The button says what it will do before it is pressed - "Undo" alone
   *  invites a click to find out, and this button moves a record. */
  get undoTooltip(): string {
    const last = this.state.undo.last;
    if (!last) return 'Nothing to undo';
    const rest = this.undoCount - 1;
    return `Move ${last.title} back to ${this.columnLabel(last.fromCode)}`
      + (rest > 0 ? ` (${rest} more move${rest > 1 ? 's' : ''} behind it)` : '');
  }

  /**
   * Take back the most recent move that has not been taken back.
   *
   * Fresh-read first: between doing and undoing, a colleague or an automated
   * path may have moved the same lead again, and yanking it back would
   * silently discard THEIR move under the name of correcting ours. So the
   * lead's current stage is read at undo time, and the step is only acted on
   * if the card is still exactly where our move left it - otherwise it is
   * dropped with an explanation, because a right to undo that has been
   * overtaken is gone, not postponed.
   *
   * The compensating move deliberately does NOT go through availableTargets:
   * that filter refuses won-outcome stages because a drag must not claim a
   * sale nobody signed - but a retraction back INTO a won stage claims
   * nothing. The contract that put the lead there is the evidence, and
   * restoring what a slip displaced is the opposite of inventing it. The
   * lead vocabulary declares no transitions server-side, so the server
   * agrees.
   *
   * An undo is not itself recorded, so it cannot be "undone" - pressing the
   * button repeatedly walks backwards through distinct moves rather than
   * flipping one lead back and forth forever.
   */
  undoLastMove(): void {
    const step = this.state.undo.last;
    if (!step || this.undoing) return;
    const backToId = this.stageId(step.fromCode);
    if (backToId == null) {
      // The stage it came from is no longer in the catalog. Nothing sane to
      // do; holding the step would pin the button on a move it can never make.
      this.state.undo.retract();
      this.snackBar?.open(
        `${step.title} came from a stage that is no longer on the board - nothing was undone.`,
        'OK', { duration: 6000 });
      return;
    }
    this.undoing = true;
    this.http.get<{ item: WorkItem }>(`/api/lead-work-items/${step.leadId}`).subscribe({
      next: detail => {
        if (detail.item.boardColumnCode !== step.toCode) {
          this.undoing = false;
          this.state.undo.retract();
          this.snackBar?.open(
            `${step.title} has been moved again since - nothing was undone.`,
            'OK', { duration: 6000 });
          return;
        }
        this.http.put(`/api/lead-pipeline/${step.leadId}/stage`, {
          leadStageId: backToId,
          // On the history this reads as what it was, not as a change of mind
          // that happens to mirror an earlier one.
          note: `Undo: back to ${this.columnLabel(step.fromCode)}`,
        }).subscribe({
          next: () => {
            this.undoing = false;
            this.state.undo.retract();
            // Redraw locally for the same reason a drop does - a reload here
            // put the card back after a visible pause, which read as the
            // button having done nothing.
            //
            // Back to the SLOT it came from, not merely the column. Returning
            // it to the bottom would leave the card somewhere it had never
            // been: a third arrangement, which is not the one being undone.
            // If the column has since shrunk past that slot the insert falls
            // through to its end, which is the nearest place left.
            const onBoard = this.items.find(row => row.leadId === step.leadId);
            if (onBoard)
              this.applyLocalMove(
                onBoard, step.fromCode, step.fromIndex, this.rowFor(onBoard).key);
            else this.load();   // not on this page's slice; only the server knows
            this.snackBar?.open(
              `${step.title} is back in ${this.columnLabel(step.fromCode)}`,
              undefined, { duration: 2500 });
          },
          error: err => {
            // The step stays: a refusal now (a WIP limit, a hiccup) is not
            // proof the retraction is wrong, and dropping it would take away
            // the retry along with the failure.
            this.undoing = false;
            this.snackBar?.open(
              err.error?.error
                ?? (err.status === 403 ? 'You do not have permission to move leads.'
                  : 'Undo failed - nothing was moved.'),
              'OK', { duration: 6000 });
          },
        });
      },
      error: () => {
        this.undoing = false;
        this.snackBar?.open(
          'Could not check where the lead is now - nothing was undone.',
          'OK', { duration: 6000 });
      },
    });
  }

  // ── List: the shared data-table in server mode (06 §5.5) ─────────────
  //
  // The List is the full-data surface, so its sort and paging run in the
  // database over the whole filtered set — the browser only ever holds one
  // page. The Board keeps its own load() lens; this fetch shares the
  // same filter state, read at call time, so table.reload() after a filter
  // change re-queries with the new values (the CRM Customers pattern).

  @ViewChild('listTable') listTable?: DataTableComponent<WorkItem>;

  /** The rows on the table's current page; export's whole-page scope. */
  private listPageRows: WorkItem[] = [];
  /** The table's page selection, as rows (its checkbox column owns the UI). */
  private listSelection: WorkItem[] = [];
  /**
   * The sort the table last rendered under.
   *
   * An export of the whole filter walks the server's pages itself, and it was
   * walking them without a sort - so a table ordered by Title exported a file
   * ordered by contribution, which is the server's default. Nothing was lost
   * (the repository always breaks ties on the row id, so the walk is stable
   * either way), but the file was not the table, which is the one thing an
   * export of a table has to be.
   */
  private listSort: DataTableServerSort | null = null;

  private readCollection<T>(params: Record<string, string | string[]>, facets = false): Observable<T> {
    const url = facets ? '/api/lead-work-items/facets' : '/api/lead-work-items';
    return this.selectedIds === null
      ? this.http.get<T>(url, { params })
      : this.http.post<T>(`${url}/query`, this.selectedIds, {
        params: this.excludeSelectedIds ? { ...params, excludeSelectedIds: 'true' } : params,
      });
  }

  fetchItems = (q: DataQuery): Observable<DataPage<WorkItem>> => {
    if (this.scopedFetch) return this.scopedFetch({ ...q, search: q.search || this.state.search });
    const params: Record<string, string | string[]> = {
      page: String(q.page), pageSize: String(q.pageSize),
    };
    if (q.sort) { params['sort'] = q.sort; params['desc'] = String(q.desc ?? false); }
    Object.assign(params, this.filterParams());
    // Client-side pruning cannot work here: List rows are database-paged.
    if (this.state.highContributionOnly)
      params['minContribution'] = String(LeadFeature.MinAnnualContribution);
    if (q.search) params['search'] = q.search;
    return this.readCollection<ListResponse>(params);
  };

  fetchList = (q: DataQuery): Observable<DataPage<WorkItem>> =>
    this.fetchItems(q).pipe(tap(res => {
      this.listPageRows = res.items;
      this.listSelection = [];
        this.listTotal = res.total;
      this.listSort = q.sort ? { field: q.sort, desc: q.desc ?? false } : null;
    }));

  onListSelection(rows: WorkItem[]): void {
    this.listSelection = rows;
  }

  deactivateLead(row: WorkItem): void {
    const name = row.title || `Lead #${row.leadId}`;
    if (!confirm(`Deactivate "${name}"? It will be hidden from the active Leads list.`))
      return;

    this.http.delete(`/api/leads/${row.leadId}`).subscribe({
      next: () => {
        this.snackBar?.open('Lead deactivated', undefined, { duration: 2500 });
        this.load();
      },
      error: error => this.snackBar?.open(
        error?.error?.error ?? 'Could not deactivate lead.',
        'OK',
        { duration: 5000 }),
    });
  }

  deactivateSelected(): void {
    const rows = [...this.listSelection];
    if (!rows.length) return;
    if (!confirm(`Deactivate ${rows.length} lead(s)? They will be hidden from the active Leads list.`))
      return;

    from(rows).pipe(
      concatMap(row => this.http.delete(`/api/leads/${row.leadId}`).pipe(
        map(() => true),
        catchError(() => of(false)))),
      toArray(),
    ).subscribe(results => {
      const failed = results.filter(success => !success).length;
      this.snackBar?.open(
        failed
          ? `Deactivated ${results.length - failed}; ${failed} could not be deactivated.`
          : `Deactivated ${results.length} lead(s).`,
        failed ? 'OK' : undefined,
        { duration: failed ? 6000 : 2500 });
      this.listTable?.clearSelection();
      this.load();
    });
  }

  /** What Export writes if nobody says otherwise: the rows that are ticked,
   *  or the page when none are. The dialog offers the alternatives. */
  get exportScope(): WorkItem[] {
    return this.listSelection.length > 0 ? this.listSelection : this.listPageRows;
  }

  /** The export's state, where the button and the specs have always read it.
   *  The behaviour behind it is shared with every other exportable table. */
  get exporting(): boolean { return this.exporter.busy; }
  get exportError(): string | null { return this.exporter.error; }

  private exportRunner?: TableExport<WorkItem>;

  /**
   * What Export does here: which rows it can reach, and what a file of them
   * looks like. How it asks, gathers and writes is the shared runner's.
   *
   * Built on first use rather than as a field initializer, because everything
   * it closes over arrives with the constructor - under ES2022 class fields an
   * initializer would run before `dialog` was assigned and capture undefined.
   */
  private get exporter(): TableExport<WorkItem> {
    return this.exportRunner ??= new TableExport<WorkItem>({
      title: 'leads',
      filename: () => `lead-work-items-${todayLocalDateString()}.csv`,
      columns: () => this.exportColumns(),
      selection: () => this.listSelection,
      pageRows: () => this.listPageRows,
      filteredTotal: () => this.listTotal,
      unfilteredTotal: () => this.unfilteredTotal(),
      pageSize: this.pageSize,
      fetchPage: (page, filtered) => this.fetchExportPage(page, filtered),
    }, this.dialog);
  }

  get canDeleteAllData(): boolean {
    return this.auth.hasPermission('Company.Manage');
  }

  /** An import or a merge changed the leads. Both views read them again - the
   *  List's page and the Board's facets would otherwise disagree about what
   *  exists. Which dialog opened, told which table, is the shared transfer
   *  actions' business. */
  onTransferChanged(): void {
    this.listTable?.reload();
    this.load();
  }

  deletingAll = false;

  deleteAllLeads(): void {
    if (this.deletingAll || !this.dialog || !this.leadApi) return;
    this.deletingAll = true;
    deleteAllData(this.dialog, this.snackBar, {
      noun: 'lead',
      run: () => this.leadApi!.deleteAllLeads(),
      reload: () => {
        this.listTable?.clearSelection();
        this.load();
      },
    }).pipe(
      finalize(() => { this.deletingAll = false; }),
    ).subscribe();
  }

  /** How many rows the table is actually showing right now. */
  get listPageCount(): number { return this.listPageRows.length; }

  /** Ask which rows, then write them. */
  openExport(): void {
    this.exporter.open();
  }

  /** Rows in the table with no filter on it, or null if the server would not
   *  say. Never errors: the caller opens a dialog either way. */
  private unfilteredTotal(): Observable<number | null> {
    return this.readCollection<ListResponse>({ page: '1', pageSize: '1' }).pipe(
      map(res => res.total as number | null),
      catchError(() => of(null)),
    );
  }

  /**
   * Writes the file for a scope the caller has already settled. Called with
   * nothing it falls back to what a ticked table means on its own, which is
   * the rows that are ticked.
   */
  exportCsv(scope?: ExportScope): void {
    this.exporter.run(scope);
  }

  /** A page for the export only: same filter as the List, but it must not
   *  disturb the table's own rows, selection or total. */
  private fetchExportPage(page: number, filtered: boolean): Observable<WorkItem[]> {
    const params: Record<string, string | string[]> = {
      page: String(page), pageSize: String(this.pageSize),
    };
    if (this.listSort) {
      params['sort'] = this.listSort.field;
      params['desc'] = String(this.listSort.desc);
    }
    // Sort is kept either way - it is how the file is ordered, not what is in
    // it. The filter is what "all the table" means to leave off.
    if (filtered) {
      Object.assign(params, this.filterParams());
      if (this.state.highContributionOnly)
        params['minContribution'] = String(LeadFeature.MinAnnualContribution);
    }
    return this.readCollection<ListResponse>(params)
      .pipe(map(res => res.items));
  }

  /**
   * What each table column writes into the file.
   *
   * Keyed by column id, so the file is built FROM `listColumns` rather than
   * from a second list beside it. The header used to be one hardcoded string,
   * and it drifted: the table grew Address, Types and Discovered and the export
   * never learned about them, while the export still said "Owner" for a column
   * the table had renamed to Sales rep.
   *
   * A column with no entry here is a build error waiting in the export spec,
   * which is the point - adding a column to the table must not be able to
   * silently leave it out of the file.
   */
  private readonly exportCells:
      Record<string, (row: WorkItem) => string | number | null | undefined> = {
    title: row => row.title ?? `Lead #${row.leadId}`,
    address: row => row.address,
    city: row => row.city,
    state: row => row.state,
    postalCode: row => row.postalCode,
    boardColumn: row => row.boardColumn,
    origin: row => row.origin,
    sources: row => this.sourceLabel(row),
    // What the table shows, like the probability below: the file is read by
    // people, and the importer folds case and spacing back onto the category
    // when the same file comes home.
    businessTypes: row => this.typeLabels(row.businessTypes),
    // The user id, which is what the importer reads back to reassign a lead.
    salesRep: row => row.salesRepUserId,
    score: row => row.qualificationScore,
    contribution: row => row.contributionBase,
    routeFit: row => row.routeFitStatus,
    nextAction: row => row.nextActionType,
    // Match what the UI shows (25%), not the raw 0.25.
    probability: row =>
      row.probability != null ? `${Math.round(row.probability * 100)}%` : '',
    discoveredAt: row => row.discoveredAt,
  };

  /**
   * The file's columns: LeadId, then the table's own, in the table's order and
   * under the table's labels.
   *
   * LEADID COMES FIRST AND IS WHY THIS FILE CAN COME BACK. Edit a few cells in
   * a spreadsheet and import it again and each row finds its own lead by that
   * number - nothing else here is stable enough to match on, since a title can
   * be edited and two leads may share one. Keep it, and keep it first: the
   * importer recognises this format by that header cell.
   *
   * Next action is the one column that is one on screen and two in the file.
   * The cell reads "Call · Aug 22" as a single fact, but the verb and the date
   * are separately editable and the importer reads them apart, so Due follows
   * it rather than being folded into it.
   */
  /**
   * The header each column is written under. A file this page exports has to
   * be a file the importer can read, and the importer talks in the schema's
   * field names rather than the labels above the columns. Columns absent from
   * this map already carry their field name as their label.
   */
  private readonly exportHeaders: Record<string, string> = {
    title: 'Name',
    address: 'AddressLine1',
    postalCode: 'PostalCode',
    businessTypes: 'BusinessTypes',
    salesRep: 'SalesRepUserId',
    contribution: 'ExpectedAnnualContribution',
    routeFit: 'RouteFit',
    nextAction: 'NextActionType',
    discoveredAt: 'DiscoveredAt',
  };

  exportColumns(): CsvColumn<WorkItem>[] {
    return [
      { header: 'LeadId', cell: row => row.leadId },
      ...this.listColumns.flatMap(column => {
        const cell = this.exportCells[column.id];
        const own = [
          { header: this.exportHeaders[column.id] ?? column.label, cell },
        ];
        return column.id === 'nextAction'
          ? [...own, {
              header: 'NextActionDue',
              cell: (row: WorkItem) => row.nextActionDue,
            }]
          : own;
      }),
      // THE FIELDS THE TABLE DOES NOT SHOW, at the end where added columns
      // land in a spreadsheet. The file is the lead, not the screen: the
      // transfer schema carries these three, so a file without them is one the
      // import reads a hole in - and which columns are HIDDEN never mattered
      // here, since the list above is the column descriptors, not the visible
      // set. Headers spell the transfer schema's own names so the round trip
      // matches without a guess.
      { header: 'Latitude', cell: row => row.lat },
      { header: 'Longitude', cell: row => row.lng },
      { header: 'ExpectedAnnualRevenue', cell: row => row.expectedAnnualRevenue },
    ];
  }

  /**
   * The Sources cell: the kinds behind this lead, not how many links there are.
   *
   * The count was the old cell and it was nearly always 1, because most leads
   * are one place. It is still on the row, still filterable and sortable, and
   * still the honest answer to "how many observations merged" — it just is not
   * what somebody reads a Sources column to find out.
   */
  sourceLabel(row: WorkItem): string {
    return leadSourceLabel(row.sourceKinds);
  }

  boardLocation(item: WorkItem): string {
    return [item.city, item.state].map(value => value?.trim()).filter(Boolean).join(', ')
      || item.address?.trim() || item.postalCode?.trim() || 'No address';
  }

  nextActionDateLabel(item: WorkItem): string | null {
    const due = item.nextActionDue;
    if (!due || stamp(due) === null) return null;
    const day = formatDate(due, 'yyyy-MM-dd', 'en-US');
    if (day === todayLocalDateString()) return 'Today';
    if (day === localDateStringOffsetDays(1)) return 'Tomorrow';
    const format = day.slice(0, 4) === todayLocalDateString().slice(0, 4) ? 'MMM d' : 'MMM d, y';
    return formatDate(due, format, 'en-US');
  }

  stageAgeLabel(item: WorkItem): string | null {
    const entered = stamp(item.stageEnteredAt);
    if (entered === null) return null;
    const days = Math.max(0, Math.floor((Date.now() - entered) / 86_400_000));
    return `${days}d`;
  }

  overdue(item: WorkItem): boolean {
    return leadActionOverdue(item.nextActionDue);
  }
}

/**
 * Whether a rule on the bar is actually narrowing anything.
 *
 * An empty rule sits on the bar as a half-written thought, so clearing it
 * would take something away and give nothing back.
 */
function hasRuleValue(
  value: DataFilterValue | DataFilterRule | undefined,
): boolean {
  if (value === null || value === undefined || value === '') return false;
  return !isRule(value) || value.value !== '';
}

function cloneFilterValues(source: DataFilterValues): DataFilterValues {
  return Object.fromEntries(Object.entries(source).map(([field, value]) => [
    field,
    isRule(value) ? { op: value.op, value: value.value } : value,
  ]));
}

function isLeadTableViewSnapshot(value: unknown): value is LeadTableViewSnapshot {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return false;
  const snapshot = value as Partial<LeadTableViewSnapshot>;
  if (typeof snapshot.ruleFilters !== 'object'
      || snapshot.ruleFilters === null
      || Array.isArray(snapshot.ruleFilters)) return false;
  return snapshot.listSort === undefined
    || (typeof snapshot.listSort === 'object'
      && snapshot.listSort !== null
      && typeof snapshot.listSort.field === 'string'
      && typeof snapshot.listSort.desc === 'boolean');
}

/**
 * An Owner rule's user id, where it is an "is" - the only comparison the map's
 * own salesRep parameter can express.
 */
function ruleUserId(held: DataFilterValue | DataFilterRule | undefined): number | null {
  if (held === null || held === undefined || held === '') return null;
  if (isRule(held) && held.op !== 'eq') return null;
  const value = Number(isRule(held) ? held.value : held);
  return Number.isFinite(value) ? value : null;
}
